/*
  MedicationsPanel — spec §12. Active + inactive medication register
  per client. Past medications are retained but visually de-emphasised.
  No auto-flagging (practitioners use exercise_implications field).
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Pill, Plus, Pencil, ArchiveRestore, Loader2, PowerOff, Power } from "lucide-react";
import { cn } from "@/lib/utils";

const SOURCES = [
  { value: "client_reported", label: "Client-reported" },
  { value: "clinician_confirmed", label: "Clinician-confirmed" },
  { value: "imported", label: "Imported" },
  { value: "migrated", label: "Migrated" },
];

function fmtDate(d) {
  if (!d) return "—";
  try {
    const p = new Date(d);
    if (!Number.isNaN(p.getTime())) return p.toLocaleDateString();
  } catch { /* fall through */ }
  return String(d);
}

export default function MedicationsPanel({ email }) {
  const { can } = useAuth();
  const canArchive = can("owner", "manager");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const encoded = encodeURIComponent(email || "");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const r = await api.get(`/clients/${encoded}/medications`);
      setRows(r.data?.medications || []);
      setCounts(r.data?.counts || { active: 0, inactive: 0 });
    } catch (e) {
      toast.error("Couldn't load medications", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [email, encoded]);

  useEffect(() => { load(); }, [load]);

  const displayed = useMemo(() =>
    showInactive ? rows : rows.filter((r) => r.active),
  [rows, showInactive]);

  async function toggleActive(row) {
    try {
      await api.patch(`/medications/${row.id}`, { active: !row.active });
      toast.success(row.active ? "Marked inactive" : "Marked active");
      await load();
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function archive(row) {
    if (!confirm(`Archive "${row.name}"? Owner/manager can restore later; audit trail stays.`)) return;
    try {
      await api.delete(`/medications/${row.id}`);
      toast.success("Archived");
      await load();
    } catch (e) {
      toast.error("Archive failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-3" data-testid="medications-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
          <Pill className="h-3.5 w-3.5" /> Medications
          {(counts.active + counts.inactive) > 0 && (
            <span className="text-zinc-400 normal-case font-normal">
              · {counts.active} active
              {counts.inactive > 0 && ` · ${counts.inactive} past`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {counts.inactive > 0 && (
            <button
              type="button"
              onClick={() => setShowInactive((s) => !s)}
              className={cn(
                "text-2xs px-2 py-1 rounded-md border transition-colors",
                showInactive
                  ? "bg-zinc-950 border-zinc-950 text-white"
                  : "border-zinc-300 text-zinc-600 hover:bg-zinc-100",
              )}
              data-testid="med-toggle-inactive"
            >
              {showInactive ? `Hide past (${counts.inactive})` : `Show past (${counts.inactive})`}
            </button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            data-testid="med-add-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add medication
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500 py-3 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : displayed.length === 0 ? (
        <div className="text-xs text-zinc-500 italic border border-dashed border-zinc-200 rounded-md p-3">
          {rows.length === 0
            ? "No medications on file. Add current or past medications with dosage, frequency and exercise implications."
            : "No active medications; toggle 'Show past' to see historic entries."}
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="med-list">
          {displayed.map((r) => (
            <li
              key={r.id}
              className={cn(
                "rounded-md border p-3 flex items-start justify-between gap-3",
                r.active ? "border-zinc-200 bg-white" : "border-zinc-200 bg-zinc-50 opacity-75",
              )}
              data-testid={`med-row-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn("text-sm font-medium", r.active ? "text-zinc-950" : "text-zinc-600 line-through")}>
                    {r.name}
                  </span>
                  {r.dosage && <span className="text-[0.75rem] text-zinc-600">{r.dosage}</span>}
                  {r.frequency && <span className="text-[0.75rem] text-zinc-500">· {r.frequency}</span>}
                </div>
                {r.purpose && <div className="text-[0.75rem] text-zinc-600 mt-0.5">For {r.purpose}</div>}
                {r.exercise_implications && (
                  <div className="text-[0.75rem] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                    <span className="text-2xs uppercase tracking-wider mr-1">EX impl.</span>
                    {r.exercise_implications}
                  </div>
                )}
                <div className="text-2xs text-zinc-500 mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                  {r.start_date && <span>Started {fmtDate(r.start_date)}</span>}
                  {r.end_date && <span>· Ended {fmtDate(r.end_date)}</span>}
                  {r.prescribing_clinician && <span>· Dr {r.prescribing_clinician}</span>}
                  {r.review_date && <span>· Review {fmtDate(r.review_date)}</span>}
                  <span className="capitalize">· {r.source?.replace("_", "-")}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => toggleActive(r)}
                  className={cn(
                    "p-1 rounded hover:bg-zinc-100",
                    r.active ? "text-zinc-600" : "text-emerald-700",
                  )}
                  title={r.active ? "Mark inactive" : "Reactivate"}
                  data-testid={`med-toggle-${r.id}`}
                >
                  {r.active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(r); setDialogOpen(true); }}
                  className="p-1 rounded hover:bg-zinc-100 text-zinc-600"
                  title="Edit"
                  data-testid={`med-edit-${r.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {canArchive && (
                  <button
                    type="button"
                    onClick={() => archive(r)}
                    className="p-1 rounded hover:bg-red-50 text-red-700"
                    title="Archive"
                    data-testid={`med-archive-${r.id}`}
                  >
                    <ArchiveRestore className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {dialogOpen && (
        <MedicationDialog
          email={email}
          existing={editing}
          onClose={() => { setDialogOpen(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
}

function MedicationDialog({ email, existing, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: existing?.name || "",
    dosage: existing?.dosage || "",
    frequency: existing?.frequency || "",
    purpose: existing?.purpose || "",
    start_date: existing?.start_date || "",
    end_date: existing?.end_date || "",
    prescribing_clinician: existing?.prescribing_clinician || "",
    exercise_implications: existing?.exercise_implications || "",
    review_date: existing?.review_date || "",
    source: existing?.source || "client_reported",
    active: existing?.active ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const change = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  async function save() {
    if (!form.name.trim()) { toast.error("Medication name required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        review_date: form.review_date || null,
      };
      if (existing?.id) {
        await api.patch(`/medications/${existing.id}`, payload);
      } else {
        await api.post(`/clients/${encodeURIComponent(email)}/medications`, payload);
      }
      toast.success(existing ? "Updated" : "Added");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="med-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit medication" : "Add medication"}</DialogTitle>
          <DialogDescription>
            Name, dosage, frequency, purpose and any exercise implications. Past
            medications stay in the record — flip inactive rather than delete.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Medication name</Label>
            <Input
              value={form.name}
              onChange={change("name")}
              placeholder="e.g. Warfarin, Prednisolone, Levodopa/carbidopa"
              className="mt-1"
              data-testid="med-field-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Dosage</Label>
              <Input value={form.dosage} onChange={change("dosage")} placeholder="5 mg" className="mt-1" data-testid="med-field-dosage" />
            </div>
            <div>
              <Label className="text-xs">Frequency</Label>
              <Input value={form.frequency} onChange={change("frequency")} placeholder="Twice daily" className="mt-1" data-testid="med-field-freq" />
            </div>
            <div>
              <Label className="text-xs">Purpose</Label>
              <Input value={form.purpose} onChange={change("purpose")} placeholder="Atrial fibrillation" className="mt-1" data-testid="med-field-purpose" />
            </div>
            <div>
              <Label className="text-xs">Prescribing clinician</Label>
              <Input value={form.prescribing_clinician} onChange={change("prescribing_clinician")} placeholder="Dr Kim" className="mt-1" data-testid="med-field-clinician" />
            </div>
            <div>
              <Label className="text-xs">Started</Label>
              <Input type="date" value={form.start_date || ""} onChange={change("start_date")} className="mt-1" data-testid="med-field-start" />
            </div>
            <div>
              <Label className="text-xs">Ended (if past)</Label>
              <Input type="date" value={form.end_date || ""} onChange={change("end_date")} className="mt-1" data-testid="med-field-end" />
            </div>
            <div>
              <Label className="text-xs">Review date</Label>
              <Input type="date" value={form.review_date || ""} onChange={change("review_date")} className="mt-1" data-testid="med-field-review" />
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <select
                value={form.source}
                onChange={change("source")}
                className="mt-1 w-full h-9 px-2 border border-zinc-300 rounded-md text-sm bg-white"
                data-testid="med-field-source"
              >
                {SOURCES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Exercise implications</Label>
            <Textarea
              rows={2}
              value={form.exercise_implications}
              onChange={change("exercise_implications")}
              placeholder="e.g. Warfarin — avoid contact/high-fall risk exercises. Corticosteroid — monitor for bone density concerns."
              className="mt-1"
              data-testid="med-field-exercise-impl"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.active}
              onChange={change("active")}
              data-testid="med-field-active"
            />
            Currently active (uncheck for past medications)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="med-save-btn">
            {saving ? "Saving…" : (existing ? "Save changes" : "Add medication")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
